import { useUserStore } from "../stores/user.store";

/**
 * Hook to check if a specific experimental feature is enabled for the current user
 *
 * This hook checks the `experimental_features` array in the user's profile to determine
 * if a feature flag is enabled. Feature flags allow gradual rollout of new features
 * to specific users before making them generally available.
 *
 * @param featureName - The name of the feature flag to check (e.g., 'courses', 'new_dashboard')
 * @returns boolean - true if the feature is enabled for the user, false otherwise
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const hasNewFeature = useFeatureFlag('new_feature');
 *
 *   if (!hasNewFeature) {
 *     return <ComingSoonPage />;
 *   }
 *
 *   return <NewFeature />;
 * };
 * ```
 *
 * Note: The experimental_features field is stored in the profiles table in the database.
 * To enable a feature for a user, add the feature name to their experimental_features array.
 *
 * On the dev server every feature is enabled, so a feature under development can
 * be worked on without seeding a flag first. Only production gates on the profile.
 */
export const useFeatureFlag = (featureName: string): boolean => {
  const { profile } = useUserStore();

  if (import.meta.env.DEV) {
    return true;
  }

  if (!profile?.experimental_features) {
    return false;
  }

  return profile.experimental_features.includes(featureName);
};
